import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  Icon,
  Kbd,
  Select,
  SelectItem,
  Text,
  useToast,
} from "@prismicio/editor-ui"
import { useMemo } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { DeferredCodeBlock as CodeBlock } from "../../DeferredCodeBlock"
import { prismicClientDocsUrl } from "../constants"
import { getSnippetArgs } from "../utils"
import {
  apiExplorerShareableUrlSearchParam,
  useAPIExplorer,
} from "./APIExplorerContext"
import { RequestFormInput, Requests, type RequestTypes } from "./Request"

export function APIExplorerRequest() {
  const { request } = useAPIExplorer()

  return (
    <Box
      display="grid"
      gridTemplateRows="minmax(0, 1fr)"
      minHeight={0}
      minWidth={0}
    >
      <Form onSubmit={request.run}>
        <Box
          display="grid"
          gridTemplateRows="auto 1fr"
          borderRadius={8}
          border
          minHeight={0}
          minWidth={0}
        >
          <Box
            as="header"
            padding={{ inline: 16 }}
            alignItems="center"
            justifyContent="space-between"
            border={{ bottom: true }}
            minHeight={48}
          >
            <Text variant="small" component="h2" color="grey9">
              Request
            </Text>
            <RequestShareButton />
          </Box>
          <Box display="flex" flexDirection="column" overflowY="auto">
            <RequestRepositoryFieldset />
            <RequestMethodFieldset />
            <ClientSnippet />
          </Box>
        </Box>
      </Form>
    </Box>
  )
}

function RequestShareButton() {
  const { request } = useAPIExplorer()
  const { showToast } = useToast()

  async function copyLink(withAccessToken: boolean) {
    const url = new URL(window.location.href)

    const state = {
      ...request,
      repository: {
        ...request.repository,
        // Provide the access token only if the user wants to share it.
        accessToken: withAccessToken
          ? request.repository.accessToken
          : undefined,
      },
      options: {
        // We only want to share the currently selected request type.
        [request.type]: request.options[request.type],
      },
    }

    url.searchParams.set(
      apiExplorerShareableUrlSearchParam,
      JSON.stringify(state),
    )
    // Add a source parameter to indicate that the link was copied from the
    // share button for analytics purposes.
    url.searchParams.set("source", "sharing")

    await navigator.clipboard.writeText(url.toString())

    showToast({
      title: withAccessToken
        ? "Request link with access token copied to clipboard"
        : "Request link copied to clipboard",
    })
  }

  useHotkeys("mod+shift+s", () => copyLink(false), {
    preventDefault: true,
    enableOnFormTags: true,
    enableOnContentEditable: true,
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button startIcon="link" color="grey">
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          endAdornment={<Kbd value={["mod", "shift", "s"]} />}
          startIcon={<Icon name="language" />}
          onSelect={() => copyLink(false)}
        >
          Copy link
          {request.repository.accessToken ? " without access token" : ""}
        </DropdownMenuItem>
        {request.repository.accessToken && (
          <DropdownMenuItem
            color="tomato"
            startIcon={<Icon name="lock" />}
            onSelect={() => copyLink(true)}
          >
            Copy link with access token
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RequestRepositoryFieldset() {
  const { request, config } = useAPIExplorer()

  return (
    <Box
      padding={16}
      border={{ bottom: true }}
      display="flex"
      flexDirection="column"
      gap={8}
    >
      <Text variant="emphasized" component="h3" color="grey11">
        Repository
      </Text>
      {!config.repository && (
        <RequestFormInput
          value={request.repository.id ?? ""}
          onValueChange={request.setRepositoryID}
          label="NAME *"
          placeholder="my-repository-name"
          error={!request.repository.id}
        />
      )}
      <RequestFormInput
        value={request.repository.accessToken ?? ""}
        onValueChange={request.setRepositoryAccessToken}
        label="ACCESS TOKEN"
        placeholder="********"
      />
    </Box>
  )
}

function RequestMethodFieldset() {
  return (
    <Box padding={16} border={{ bottom: true }} display="grid" gap={16}>
      <RequestTypeSelect />
      <RequestParamsFieldset />
    </Box>
  )
}

function RequestTypeSelect() {
  const { request } = useAPIExplorer()

  return (
    <Select
      color="grey"
      size="large"
      side="bottom"
      flexContent
      boldTriggerText
      endIcon="unfoldMore"
      value={request.type}
      onValueChange={(type: RequestTypes) => request.setRequestType(type)}
    >
      {Object.values(Requests).map(({ type }) => (
        <SelectItem key={type} value={type}>
          {type}
        </SelectItem>
      ))}
    </Select>
  )
}

function RequestParamsFieldset() {
  const { request } = useAPIExplorer()

  const RequestFieldset = Requests[request.type].Fieldset
  if (!RequestFieldset) {
    return null
  }

  return (
    <Box display="flex" flexDirection="column" gap={8}>
      <Text variant="emphasized" component="h3" color="grey11">
        Params
      </Text>
      <RequestFieldset />
    </Box>
  )
}

function ClientSnippet() {
  const { request } = useAPIExplorer()

  // Update the request snippet whenever the request properties change
  const snippet = useMemo(() => {
    const createClientSnippet = `const client = createClient(${getSnippetArgs(
      request.repository.id || Symbol("repositoryName"),
      request.repository.accessToken
        ? { accessToken: Symbol("process.env.PRISMIC_ACCESS_TOKEN") }
        : undefined,
    )})`

    const requestSnippet = Requests[request.type].getSnippet({
      ...request.options[request.type],
      ...request.pagination,
      lang: request.lang,
      ref: request.ref,
    })

    return `${createClientSnippet}\n\nconst response = await ${requestSnippet}`
  }, [
    request.repository,
    request.type,
    request.options,
    request.pagination,
    request.lang,
    request.ref,
  ])

  return (
    <Box display="flex" flexDirection="column" padding={16} gap={8}>
      <Text variant="emphasized" component="h3" color="grey11">
        Client code
      </Text>
      <CodeBlock
        title="request.ts"
        language="typescript"
        code={snippet}
        showLineNumbers={false}
      />
      <Text>
        Learn more on{" "}
        <Text
          component="a"
          href={prismicClientDocsUrl}
          target="blank"
          underline
        >
          @prismicio/client documentation
        </Text>
        .
      </Text>
    </Box>
  )
}
